import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
} from 'react-native';
import FontAwesome from '@react-native-vector-icons/fontawesome';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import LanguageChips from './LanguageChips';
import { RootState } from '../redux/store';
import { clearCache } from '../redux/services/authSlice';
import { Alert, Platform } from 'react-native';
import { ShowToast } from '../Common/common';
import { permittedReportCharts } from '../Screens/ReportCharts/registry';
import { useGetPendingAuthorizationsQuery } from '../redux/services/foauthorizationApi';
import { usePhoneticEnabled } from '../utils/transliterate/usePhoneticInput';

const drawerItems = [
  { key: 'dashboard', title: 'Dashboard', icon: 'dashboard', screen: 'Dashboard' },
  { key: 'roomStatus', title: 'Room Status', icon: 'bed', screen: 'StatusScreen' },
  { key: 'hkStatus', title: 'HK Status', icon: 'clipboard', screen: 'HKStatusScreen' },
  { key: 'hkProducts', title: 'HK Products', icon: 'cubes', screen: 'HKProducts' },
  { key: 'stProducts', title: 'Store Products', icon: 'cube', screen: 'STProducts' },
  { key: 'housekeeping', title: 'Housekeeping', icon: 'paint-brush', screen: 'HouseKeeping' },
  { key: 'linkRooms', title: 'Link Rooms', icon: 'link', screen: 'LinkRooms' },
  { key: 'pendingAuthorizations', title: 'Pending Authorizations', icon: 'gavel', screen: 'PendingAuthorizations' },
  { key: 'roomAvailability', title: 'Room Availability', icon: 'building', screen: 'RoomAvailability' },
  { key: 'inHouse', title: 'In-house', icon: 'users', screen: 'InHouse' },
  { key: 'reportCharts', title: 'Report Charts', icon: 'area-chart', screen: 'ReportCharts' },
  { key: 'revenueSummary', title: 'Revenue Summary', icon: 'bar-chart', screen: 'RevenueSummary' },
  { key: 'assetManagement', title: 'Asset Management', icon: 'archive', screen: 'AssetHome' },
  { key: 'purchaseOrder', title: 'Purchase Order', icon: 'list-alt', screen: 'PurchaseOrder' },
  { key: 'purchase', title: 'Purchase', icon: 'shopping-cart', screen: 'Purchase' },
  { key: 'indent', title: 'Indent', icon: 'file-text-o', screen: 'Indent' },
  { key: 'foodProduction', title: 'Food Production', icon: 'cutlery', screen: 'FPProductionEntry' },
  { key: 'productionList', title: 'Production List', icon: 'list', screen: 'FPProductionList' },
  { key: 'caption', title: 'Caption', icon: 'user', screen: 'Home' },
  { key: 'tables', title: 'Tables', icon: 'table', screen: 'Tables' },
  { key: 'products', title: 'Products', icon: 'cutlery', screen: 'Products' },
  { key: 'cancelKot', title: 'Cancel KOT', icon: 'times-circle', screen: 'CancelKOT' },
  { key: 'changeTable', title: 'Change Table', icon: 'refresh', screen: 'ChangeTable' },
  { key: 'bill', title: 'Bill', icon: 'file-text', screen: 'Bill' },
  { key: 'settlement', title: 'Settlement', icon: 'money', screen: 'Settlement' },
  {
    key: 'runningOrders',
    title: 'Running Orders',
    icon: 'clock-o',
    screen: 'RunningOrders',
  },
  { key: 'productBlock', title: 'Product Block', icon: 'ban', screen: 'ProductBlock' },
  { key: 'notifications', title: 'Notifications', icon: 'bell', screen: 'Notifications' },
  { key: 'userGuide', title: 'Help / User Guide', icon: 'question-circle', screen: 'Help' },
];

const screenLinks: { [key: string]: string } = {
  StatusScreen: '/status/status',
  HKStatusScreen: '/status/hkstatus',
  HKProducts: '/masters/hkproduct',
  STProducts: '/masters/stproduct',
  HouseKeeping: '/transaction/fohousekeeping',
  LinkRooms: '/transaction/folinkrooms',
  PendingAuthorizations: '/admin/pending-authorizations',
  RoomAvailability: '/status/status',
  InHouse: '/status/status',
  RevenueSummary: '/reports/forevenue',
  AssetHome: '/masters/am-categories',
  PurchaseOrder: '/transaction/stpurchaseorder',
  Purchase: '/transaction/stpurchase',
  Indent: '/transaction/stindent',
  FPProductionEntry: '/transaction/fpproductionentry',
  FPProductionList: '/transaction/fpproductionlist',
  Home: '/transaction/potablelist',
  Tables: '/transaction/potablelist',
  Products: '/transaction/pokot',
  CancelKOT: '/transaction/pokot',
  ChangeTable: '/transaction/potablelist',
  Bill: '/transaction/potablelist',
  Settlement: '/transaction/posettle',
  RunningOrders: '/transaction/porunningordes',
  ProductBlock: '/masters/poproductblock',
  Notifications: 'always', // always shown
  Help: 'always', // always shown
  PublicPlacesScreen: '/status/publicareas',
};

const CustomDrawer = ({ navigation, onClose }: any) => {
  const { t, i18n } = useTranslation('drawer');
  const [phoneticOn, setPhoneticOn] = usePhoneticEnabled();
  const gdata = useSelector((state: RootState) => state.gdata);
  const dispatch = useDispatch();

  const hasFOAccess = gdata.menu?.some(
    (v: any) => v.mn_link === '/status/status' || v.mn_link === '/admin/company'
  );
  const hasAuthPermission =
    hasFOAccess ||
    gdata.menu?.some(
      (v: any) => v.mn_link === '/admin/pending-authorizations'
    );

  const { data: pendingAuths = [] } = useGetPendingAuthorizationsQuery(
    undefined,
    { skip: !hasAuthPermission }
  );
  const pendingCount = Array.isArray(pendingAuths) ? pendingAuths.length : 0;

  const handleClearCache = () => {
    const confirmMsg = t('confirmClearCache', {
      defaultValue:
        'Are you sure you want to clear all cached data? This will not log you out.'
    });
    const successMsg = t('cacheCleared', {
      defaultValue: 'Cache cleared successfully'
    });
    if (Platform.OS === 'web') {
      if (window.confirm(confirmMsg)) {
        dispatch(clearCache());
        ShowToast(successMsg);
        onClose?.();
      }
      return;
    }

    Alert.alert(
      t('clearCache', { defaultValue: 'Clear Cache' }),
      confirmMsg,
      [
        { text: t('cancel', { defaultValue: 'Cancel' }), style: 'cancel' },
        {
          text: t('clear', { defaultValue: 'Clear' }),
          style: 'destructive',
          onPress: () => {
            dispatch(clearCache());
            ShowToast(successMsg);
            onClose?.();
          },
        },
      ],
    );
  };

  const goTo = (screen: string) => {
    const nav =
      navigation && typeof navigation.navigate === 'function'
        ? navigation
        : navigation?.current && typeof navigation.current.navigate === 'function'
        ? navigation.current
        : null;

    if (nav) {
      nav.navigate(screen);
    } else {
      console.warn('Navigation not ready for screen:', screen);
    }
    onClose?.();
  };

  const isAllowed = (screen: string) => {
    if (screen === 'PendingAuthorizations') {
      return Boolean(hasAuthPermission);
    }
    if (screen === 'LinkRooms') {
      return Boolean(
        hasFOAccess ||
        gdata?.menu?.some((v: any) => v.mn_link === '/transaction/folinkrooms')
      );
    }
    if (screen === 'HKStatusScreen') {
      return Boolean(
        hasFOAccess ||
        gdata?.menu?.some(
          (v: any) =>
            v.mn_link === '/status/hkstatus' ||
            v.mn_link === '/status/status' ||
            v.mn_link === '/transaction/fohousekeeping'
        )
      );
    }
    if (screen === 'HouseKeeping') {
      return Boolean(
        hasFOAccess ||
        gdata?.menu?.some(
          (v: any) =>
            v.mn_link === '/transaction/fohousekeeping' ||
            v.mn_link === '/status/hkstatus' ||
            v.mn_link === '/transaction/publiccleaning' ||
            v.mn_link === '/masters/hkpublicplace' ||
            v.mn_link?.includes('publiccleaning')
        )
      );
    }
    if (screen === 'AssetHome') {
      return Boolean(
        gdata?.menu?.some(
          (v: any) =>
            v.mn_link?.includes('/am') ||
            v.mn_link?.includes('am-') ||
            v.mn_link === '/masters/am-categories' ||
            v.mn_link === '/transaction/am-assets'
        )
      );
    }
    if (screen === 'ReportCharts') {
      return permittedReportCharts(gdata?.menu).length > 0;
    }
    const link = screenLinks[screen];
    return (
      link === 'always' || gdata?.menu?.some((v: any) => v.mn_link === link)
    );
  };

  return (
    <View style={styles.drawer}>
      <View style={styles.headerSticky}>
        <Text style={styles.drawerTitle}>
          {t('welcome', { defaultValue: 'Welcome' })} {gdata.username}
        </Text>
      </View>

      <ScrollView contentContainerStyle={styles.scrollContent}>
        {drawerItems
          .filter(item => isAllowed(item.screen))
          .map((item, idx) => (
            <TouchableOpacity
              key={idx}
              style={styles.drawerItem}
              onPress={() => goTo(item.screen)}
            >
              <FontAwesome
                name={item.icon as any}
                size={20}
                style={styles.drawerIcon}
              />
              <Text style={styles.drawerItemText}>
                {t(item.key, { defaultValue: item.title })}
              </Text>
              {item.screen === 'PendingAuthorizations' && pendingCount > 0 && (
                <View style={styles.badgePill}>
                  <Text style={styles.badgePillText}>{pendingCount}</Text>
                </View>
              )}
            </TouchableOpacity>
          ))}
      </ScrollView>

      <View style={styles.langSelector}>
        <FontAwesome
          name="globe"
          size={16}
          color="#666"
          style={{ marginRight: 8 }}
        />
        <LanguageChips />
      </View>

      <TouchableOpacity
        style={styles.footer}
        onPress={() => setPhoneticOn(!phoneticOn)}
      >
        <FontAwesome name="keyboard-o" size={18} color="#666" />
        <Text style={styles.footerText}>
          {`Type English → தமிழ்/മലയാളം: ${phoneticOn ? 'ON' : 'OFF'}`}
        </Text>
      </TouchableOpacity>

      <TouchableOpacity style={styles.footer} onPress={handleClearCache}>
        <FontAwesome name="trash" size={18} color="#d9534f" />
        <Text style={[styles.footerText, { color: '#d9534f' }]}>
          {t('clearCache', { defaultValue: 'Clear Cache' })}
        </Text>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  drawer: {
    flex: 1,
    width: 240,
    backgroundColor: '#f8f9fa',
    borderRightWidth: 1,
    borderColor: '#ddd',
    justifyContent: 'space-between',
  },
  headerSticky: {
    paddingHorizontal: 10,
    paddingTop: 10,
    backgroundColor: '#f8f9fa',
    borderBottomWidth: 1,
    borderColor: '#eee',
  },
  drawerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 10,
    paddingLeft: 5,
  },
  scrollContent: {
    paddingHorizontal: 10,
  },
  drawerItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 5,
    borderBottomWidth: 0.5,
    borderColor: '#eee',
  },
  drawerIcon: {
    width: 24,
    marginRight: 10,
    textAlign: 'center',
  },
  drawerItemText: {
    fontSize: 16,
  },
  badgePill: {
    marginLeft: 'auto',
    backgroundColor: '#EF4444',
    borderRadius: 10,
    paddingHorizontal: 7,
    paddingVertical: 2,
    minWidth: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgePillText: {
    color: '#FFFFFF',
    fontSize: 11,
    fontWeight: 'bold',
  },
  langSelector: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderTopWidth: 1,
    borderColor: '#eee',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    justifyContent: 'space-between',
  },
  langButtonsRow: {
    flexDirection: 'row',
  },
  langButton: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 6,
    backgroundColor: '#f0f0f0',
    marginLeft: 4,
  },
  langButtonActive: {
    backgroundColor: '#2563EB',
  },
  langButtonText: {
    fontSize: 12,
    color: '#444',
    fontWeight: '500',
  },
  langButtonTextActive: {
    color: '#FFFFFF',
    fontWeight: 'bold',
  },
  footer: {
    padding: 12,
    borderTopWidth: 1,
    borderColor: '#ccc',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#e9ecef',
    justifyContent: 'center',
  },
  footerText: {
    marginLeft: 8,
    fontWeight: 'bold',
    fontSize: 16,
  },
});

export default CustomDrawer;
